import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import {
  NotificationsService,
  NotificationTab,
  NotificationTimeframe,
  TrayNotification,
} from '@core/services/notifications';
import { BulkUploadSocketService, NotificationChange } from '@core/services/bulk-upload-socket.service';
import { ToastService } from '@core/services/toaster';
import { Auth } from '@core/auth/auth';
import { environment } from '@env/environment';
import { Subscription } from 'rxjs';
import { notificationDestination } from './notification-destination';
import { InboxApprovalActions, InboxDecision } from './inbox-approval-actions';
import {
  ApprovalActionConfirmPayload,
  ApprovalActionModalComponent,
} from '@shared/components/approval-action-modal/approval-action-modal';

export const TIMEFRAME_OPTIONS: { value: NotificationTimeframe; label: string }[] = [
  { value: 7, label: 'Last 7 Days' },
  { value: 30, label: 'Last 30 Days' },
  { value: 60, label: 'Last 60 Days' },
  { value: 90, label: 'Last 90 Days' },
];

const DEFAULT_TIMEFRAME: NotificationTimeframe = 30;
const PAGE_SIZE = 20;
const TITLE_MAX = 100;
const DESCRIPTION_MAX = 250;

const EMPTY_MESSAGES: Record<NotificationTab, string> = {
  all: 'No notifications are available for the selected period.',
  unread: 'No unread notifications are available for the selected period.',
  inbox: 'No approval notifications are available for the selected period.',
};

interface NotificationGroup {
  label: string;
  rows: TrayNotification[];
}

/**
 * Global notification bell + tray (US 13507-13510), rendered once in the shared header.
 *
 * Every list, count and read state comes from the server; nothing is kept only in the
 * browser. The badge shows the server's total unread count (all types, all dates), which is
 * independent of the tray's tab and timeframe. Socket pushes carry only that count, so a
 * repeated or missed push can never make the badge drift - it is always replaced, never
 * incremented.
 */
@Component({
  selector: 'app-notifications',
  imports: [CommonModule, ApprovalActionModalComponent],
  templateUrl: './notifications.html',
  styleUrl: './notifications.scss',
})
export class Notifications implements OnInit, OnDestroy {
  @ViewChild('notificationWrapper') notificationWrapper!: ElementRef;

  /** Kept for the header binding; tray dates always use DD-MM-YYYY HH:mm. */
  @Input() dateFormat = 'dd/MM/yyyy';

  @Output() opened = new EventEmitter<void>();

  private readonly router = inject(Router);
  private readonly notify = inject(NotificationsService);
  private readonly socket = inject(BulkUploadSocketService);
  private readonly auth = inject(Auth);
  private readonly toaster = inject(ToastService);
  private readonly inboxActions = inject(InboxApprovalActions);

  readonly timeframeOptions = TIMEFRAME_OPTIONS;
  readonly dateTimeFormat = 'dd-MM-yyyy HH:mm';

  isPanelOpen = false;
  activeTab: NotificationTab = 'all';
  timeframe: NotificationTimeframe = DEFAULT_TIMEFRAME;
  isApprover = false;

  rows: TrayNotification[] = [];
  groups: NotificationGroup[] = [];
  page = 0;
  hasMore = false;
  loading = false;
  loadError = '';
  unreadInView = 0;
  unreadCount = 0;
  markAllBusy = false;
  /** Rows with a read-state request in flight - their buttons are disabled meanwhile. */
  busyIds = new Set<string>();

  /** Inbox Approve / Reject through the shared approval-action modal. */
  decisionRow: TrayNotification | null = null;
  decisionType: InboxDecision = 'approve';
  decisionSubmitting = false;

  private requestSeq = 0;
  private readonly subs = new Subscription();

  get badgeLabel(): string {
    return this.unreadCount > 99 ? '99+' : String(this.unreadCount);
  }

  get emptyMessage(): string {
    return EMPTY_MESSAGES[this.activeTab];
  }

  ngOnInit(): void {
    this.refreshSummary();
    this.socket.subscribeNotifications(this.auth.currentUser()?.accessToken);

    this.subs.add(this.socket.notificationChanges().subscribe((change) => this.onServerChange(change)));
    // Bulk upload / payroll results still arrive over the legacy socket channel.
    this.subs.add(
      this.notify.notificationUpdated$.subscribe(() => {
        this.refreshSummary();
        if (this.isPanelOpen) this.loadPage(0);
      }),
    );
    this.subs.add(
      this.auth.loggedIn$.subscribe((isLoggedIn) => {
        if (!isLoggedIn) {
          this.socket.unsubscribeNotifications();
          this.reset();
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.isPanelOpen && this.notificationWrapper && !this.notificationWrapper.nativeElement.contains(event.target)) {
      this.closePanel();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.decisionRow) {
      this.closeDecision();
      return;
    }
    if (this.isPanelOpen) this.closePanel();
  }

  toggleNotification(): void {
    if (this.isPanelOpen) {
      this.closePanel();
      return;
    }
    this.isPanelOpen = true;
    // Every open starts from All + Last 30 Days and re-checks approver designation.
    this.activeTab = 'all';
    this.timeframe = DEFAULT_TIMEFRAME;
    this.rows = [];
    this.groups = [];
    this.refreshSummary();
    this.loadPage(0);
    this.opened.emit();
  }

  /** Closing never changes read state. */
  closePanel(): void {
    this.isPanelOpen = false;
  }

  closeNotificationPanel(): void {
    this.closePanel();
  }

  selectTab(tab: NotificationTab): void {
    if (tab === this.activeTab) return;
    this.activeTab = tab;
    this.loadPage(0);
  }

  selectTimeframe(value: string | number): void {
    this.timeframe = Number(value) as NotificationTimeframe;
    this.loadPage(0);
  }

  loadMore(): void {
    if (this.hasMore && !this.loading) this.loadPage(this.page + 1);
  }

  private refreshSummary(): void {
    if (!this.auth.currentUser()) return;
    this.notify.getSummary().subscribe({
      next: (summary) => {
        this.unreadCount = summary?.unreadCount ?? 0;
        this.isApprover = !!summary?.isApprover;
        if (!this.isApprover && this.activeTab === 'inbox') {
          this.activeTab = 'all';
          if (this.isPanelOpen) this.loadPage(0);
        }
      },
      error: () => {
        // Keep the last known count; the next push or open re-syncs it.
      },
    });
  }

  private loadPage(page: number): void {
    const seq = ++this.requestSeq;
    this.loading = true;
    this.loadError = '';
    if (page === 0) {
      this.rows = [];
      this.groups = [];
    }

    this.notify.getTray(this.activeTab, this.timeframe, page, PAGE_SIZE).subscribe({
      next: (data) => {
        if (seq !== this.requestSeq) return; // a newer tab/timeframe request superseded this one
        this.loading = false;
        this.page = page;
        this.rows = page === 0 ? data.rows : [...this.rows, ...data.rows];
        this.hasMore = data.hasMore;
        this.unreadInView = data.unreadInView;
        this.unreadCount = data.unreadCount;
        this.regroup();
      },
      error: (err) => {
        if (seq !== this.requestSeq) return;
        this.loading = false;
        if (err?.status === 403 && this.activeTab === 'inbox') {
          // Approver designation was removed since the tray opened.
          this.isApprover = false;
          this.activeTab = 'all';
          this.loadPage(0);
          return;
        }
        this.loadError = err?.error?.message || 'Unable to load notifications. Please try again.';
      },
    });
  }

  private onServerChange(change: NotificationChange): void {
    if (typeof change?.unreadCount === 'number') {
      this.unreadCount = change.unreadCount;
    } else {
      // (Re)subscribed after connect/reconnect: whatever was pushed meanwhile may be lost.
      this.refreshSummary();
    }
    if (this.isPanelOpen && !this.loading && this.busyIds.size === 0 && !this.markAllBusy && !this.decisionSubmitting) {
      this.loadPage(0);
    }
  }

  toggleRead(row: TrayNotification, event?: Event): void {
    event?.stopPropagation();
    if (this.busyIds.has(row._id)) return;
    const target = !row.isRead;
    this.busyIds.add(row._id);

    this.notify.setReadState(row._id, target).subscribe({
      next: ({ notification, unreadCount }) => {
        this.busyIds.delete(row._id);
        this.unreadCount = unreadCount;
        this.applyReadState(row._id, notification?.isRead ?? target);
        this.reloadIfLoading();
      },
      error: (err) => {
        this.busyIds.delete(row._id);
        // The stored state is unchanged, so the row is left exactly as it was.
        this.toaster.error(err?.error?.message || `Unable to mark this notification as ${target ? 'read' : 'unread'}.`);
      },
    });
  }

  markAllAsRead(): void {
    if (this.markAllBusy || this.unreadInView === 0) return;
    this.markAllBusy = true;
    this.notify.markAllReadInView(this.activeTab, this.timeframe).subscribe({
      next: ({ unreadCount }) => {
        this.markAllBusy = false;
        this.unreadCount = unreadCount;
        this.loadPage(0);
      },
      error: (err) => {
        this.markAllBusy = false;
        this.toaster.error(err?.error?.message || 'Unable to mark notifications as read.');
        this.loadPage(0);
      },
    });
  }

  clearAllNotifications(): void {
    if (!confirm('Clear all notifications? They will be removed from your tray.')) return;
    const user = this.auth.currentUser();
    this.notify.clearAll(String(user?.id ?? '')).subscribe({
      next: () => {
        this.refreshSummary();
        this.loadPage(0);
      },
      error: (err) => this.toaster.error(err?.error?.message || 'Unable to clear notifications.'),
    });
  }

  /** Opening verifies ownership and access on the server, marks it read, then navigates. */
  openNotification(row: TrayNotification): void {
    if (this.busyIds.has(row._id)) return;
    this.busyIds.add(row._id);

    this.notify.openNotification(row._id).subscribe({
      next: (result) => {
        this.busyIds.delete(row._id);
        this.unreadCount = result.unreadCount;
        this.applyReadState(row._id, true);
        this.reloadIfLoading();

        if (!result.available) {
          this.toaster.warning('Unavailable', result.message || 'This item is no longer available.');
          return;
        }

        if (!result.destination.notificationType) {
          this.openLegacy(row);
          return;
        }

        const route = notificationDestination(result.destination);
        if (route) {
          this.closePanel();
          this.router.navigateByUrl(route);
        } else {
          this.toaster.info('No page to open', 'This notification has no related page to open.');
        }
      },
      error: (err) => {
        this.busyIds.delete(row._id);
        this.toaster.error(err?.error?.message || 'Unable to open this notification.');
      },
    });
  }

  /** Inline Approve / Reject is offered only for a request pending with this user, in a module wired for it. */
  canDecide(row: TrayNotification): boolean {
    if (this.activeTab !== 'inbox' || !row.inbox?.actionable || !this.inboxActions.supports(row.referenceModule)) {
      return false;
    }
    const user = this.auth.currentUser();
    const requesterId = row.inbox?.requesterId;
    if (requesterId !== undefined && requesterId !== null && requesterId !== '') {
      const myIds = [user?.id, user?.employee_login_id].filter((v) => v !== undefined && v !== null).map(String);
      return !myIds.includes(String(requesterId));
    }
    // The API sent no requester id: only an exact name match counts as the user's own request.
    const reqName = row.inbox?.requesterName?.trim().toLowerCase();
    const myNames = [user?.name, user?.firstname, user?.username]
      .filter((n): n is string => Boolean(n))
      .map((n) => n.trim().toLowerCase());
    return !(reqName && myNames.includes(reqName));
  }

  /** A page load that was in flight while a row changed may carry stale rows: discard it and fetch fresh. */
  private reloadIfLoading(): void {
    if (this.loading) this.loadPage(0);
  }

  pendingSinceLabel(row: TrayNotification): string {
    const days = Math.floor((Date.now() - new Date(row.createdAt).getTime()) / (24 * 60 * 60 * 1000));
    if (!Number.isFinite(days) || days <= 0) return 'Pending since today';
    return `Pending since ${days} day${days === 1 ? '' : 's'}`;
  }

  openDecision(row: TrayNotification, decision: InboxDecision, event?: Event): void {
    event?.stopPropagation();
    if (!this.canDecide(row) || this.decisionSubmitting) return;
    this.decisionRow = row;
    this.decisionType = decision;
  }

  closeDecision(): void {
    if (this.decisionSubmitting) return;
    this.decisionRow = null;
  }

  confirmDecision(payload: ApprovalActionConfirmPayload): void {
    const row = this.decisionRow;
    if (!row || this.decisionSubmitting || !row.referenceModule || !row.referenceId) return;
    const decision = this.decisionType;
    this.decisionSubmitting = true;

    this.inboxActions.decide(row.referenceModule, row.referenceId, decision, payload.reason).subscribe({
      next: (resp: any) => {
        this.decisionSubmitting = false;
        this.decisionRow = null;
        this.toaster.success(resp?.message || `Request ${decision === 'approve' ? 'approved' : 'rejected'} successfully`);
        // The request has left this approver's queue: mark its notification
        // read, then reload the Inbox and the badge from the server.
        const reload = () => {
          this.refreshSummary();
          this.loadPage(0);
        };
        if (row.isRead) {
          reload();
        } else {
          this.notify.setReadState(row._id, true).subscribe({ next: reload, error: reload });
        }
      },
      error: (err: any) => {
        this.decisionSubmitting = false;
        this.decisionRow = null;
        // e.g. 403 "not authorized", 400 "already processed": the server's reason is shown,
        // and the list is reloaded so a request that is no longer actionable drops out.
        this.toaster.error(err?.error?.message || `Unable to ${decision} this request.`);
        this.loadPage(0);
      },
    });
  }

  /** Bulk upload result tone, from the message the backend stored. */
  bulkUploadTone(row: TrayNotification): 'failed' | 'success' | null {
    if (row.notificationType || row.moduleType !== 'Bulk Upload') return null;
    const message = (row.message || '').toLowerCase();
    if (/upload failed|data upload failed|validation failed/.test(message)) return 'failed';
    if (/completed successfully|uploaded successfully/.test(message)) return 'success';
    return null;
  }

  /** System notifications written before US 13430 (bulk upload, policy export, payroll). */
  private openLegacy(msg: TrayNotification): void {
    const { moduleType, actionUrl, reportUrl } = msg;
    if (moduleType === 'Bulk Upload' || moduleType === 'POLICY_EXPORT') {
      const report = this.reportLink(msg);
      if (report) this.downloadNotificationFile(report);
    } else if (moduleType === 'PAYROLL') {
      if (reportUrl) this.downloadNotificationFile(reportUrl);
      if (actionUrl) {
        this.closePanel();
        setTimeout(() => this.router.navigateByUrl(actionUrl), 1200);
      }
    } else if (actionUrl) {
      this.closePanel();
      this.router.navigateByUrl(actionUrl);
    }
  }

  private applyReadState(id: string, isRead: boolean): void {
    const row = this.rows.find((r) => r._id === id);
    if (!row || row.isRead === isRead) return;
    row.isRead = isRead;
    this.unreadInView = Math.max(0, this.unreadInView + (isRead ? -1 : 1));
    if (this.activeTab === 'unread' && isRead) {
      this.rows = this.rows.filter((r) => r._id !== id);
    }
    this.regroup();
  }

  private groupLabel(created: Date, today: Date, yesterday: Date): string {
    if (created >= today) return 'Today';
    if (created >= yesterday) return 'Yesterday';
    return 'Earlier';
  }

  private regroup(): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
    const groups: NotificationGroup[] = [];
    for (const row of this.rows) {
      const label = this.groupLabel(new Date(row.createdAt), today, yesterday);
      const last = groups[groups.length - 1];
      if (last?.label === label) last.rows.push(row);
      else groups.push({ label, rows: [row] });
    }
    this.groups = groups;
  }

  private reset(): void {
    this.isPanelOpen = false;
    this.rows = [];
    this.groups = [];
    this.unreadCount = 0;
    this.unreadInView = 0;
    this.isApprover = false;
  }

  truncate(text: string | null | undefined, max: number): string {
    const value = text ?? '';
    return value.length > max ? `${value.slice(0, max - 1)}…` : value;
  }

  title(row: TrayNotification): string {
    return this.truncate(row.title, TITLE_MAX);
  }

  description(row: TrayNotification): string {
    return this.truncate(row.message, DESCRIPTION_MAX);
  }

  isLegacyDownload(row: TrayNotification): boolean {
    return !row.notificationType && (row.moduleType === 'Bulk Upload' || row.moduleType === 'POLICY_EXPORT');
  }

  trackById = (_: number, row: TrayNotification): string => row._id;

  // The bulk-upload error/report file link comes back under different keys
  // depending on which backend flow wrote the notification: an employee-profile
  // migration failure nests it at `metadata.errorReportUrl`; `error_file_url`/
  // `errorFileUrl` is the admin-master bulk upload convention; `download_url` the
  // template-download API; `actionUrl`/`reportUrl`/`failedFileUrl` other shapes.
  reportLink(msg: any): string {
    return (
      msg?.actionUrl ||
      msg?.reportUrl ||
      msg?.errorReportUrl ||
      msg?.error_file_url ||
      msg?.errorFileUrl ||
      msg?.download_url ||
      msg?.downloadUrl ||
      msg?.metadata?.url ||
      msg?.metadata?.errorReportUrl ||
      msg?.metadata?.failedFileUrl ||
      msg?.metadata?.error_file_url ||
      msg?.metadata?.errorFileUrl ||
      msg?.metadata?.download_url ||
      msg?.metadata?.downloadUrl ||
      ''
    );
  }

  downloadNotificationFile(fileUrl: string, event?: Event): void {
    event?.stopPropagation();
    if (!fileUrl) return;

    let fullUrl = fileUrl;
    if (!fileUrl.startsWith('http://') && !fileUrl.startsWith('https://')) {
      const baseUrl = environment.fileUrl.replace(/\/+$/, '');
      const relPath = fileUrl.replace(/^\/+/, '');
      fullUrl = `${baseUrl}/${relPath}`;
    }

    const rawName = fullUrl.split('/').pop()?.split('?')[0] || 'download.xlsx';
    const filename = decodeURIComponent(rawName).replace(/^\d+_/, '');

    const link = document.createElement('a');
    link.href = fullUrl;
    link.download = filename;
    link.target = '_blank';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
